// pages/WebsiteLinks/AddBulkWebsiteLinks.js
import React, { useState } from "react";
import axios from "axios";
import backendURL from "../../config";

export default function AddBulkWebsiteLinks({ closeModal, handleRefresh }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "text/plain") {
      setSelectedFile(file);
      setError(null);
      setResults([]); // Clear previous results on new file selection
    } else {
      setSelectedFile(null);
      setError("Please upload a valid .txt file.");
      setResults([]);
    }
  };

  const handleProcessFile = () => {
    if (!selectedFile) {
      setError("Please select a file first.");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target.result;
      // Parse links from comma-separated or line-separated text
      const links = text.replace(/,/g, '\n').split('\n').map(link => link.trim()).filter(link => link);
      
      if (links.length === 0) {
        setError("No valid links found in the file.");
        return;
      }
      
      setIsProcessing(true);
      setError(null);
      // Initialize results with a 'pending' status for each link
      setResults(links.map(link => ({ link, status: 'pending', message: 'Waiting...' })));

      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Process each link sequentially to show live feedback
      for (const link of links) {
        try {
          const response = await axios.post(`${backendURL}/add-website-link`, { website_link: link }, { headers });
          // Update result for this link on success
          setResults(prevResults => prevResults.map(r => 
            r.link === link ? { ...r, status: 'success', message: response.data.message || "Success" } : r
          ));
        } catch (err) {
          // Update result for this link on error
          setResults(prevResults => prevResults.map(r => 
            r.link === link ? { ...r, status: 'error', message: err.response?.data?.message || "Error" } : r
          ));
        }
      }
      
      handleRefresh(); // Refresh the main list in the parent component
      setIsProcessing(false);
    };

    reader.readAsText(selectedFile);
  };

  return (
    <div>
      <h3 className="h3 d-inline align-middle">
        <strong>Add Bulk Website Links</strong>
      </h3>
      <div className="card mt-3">
        <div className="card-body">
          <h5 className="card-title mb-2">
            Upload a .txt file with comma-separated or line-separated website links.
          </h5>
          <div className="mb-3">
            <input 
              type="file" 
              className="form-control" 
              accept=".txt" 
              onChange={handleFileChange} 
              disabled={isProcessing} 
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={handleProcessFile}
            disabled={!selectedFile || isProcessing}
          >
            {isProcessing ? (
              <><i className="fa fa-spinner fa-spin me-1"></i> Processing...</>
            ) : ( "Upload and Process Links" )}
          </button>

          {error && <div className="alert alert-danger mt-3">{error}</div>}

          {results.length > 0 && (
            <div className="mt-4">
              <h5>Processing Results:</h5>
              <ul className="list-group" style={{maxHeight: '30vh', overflowY: 'auto'}}>
                {results.map((result, index) => (
                  <li key={index} className="list-group-item d-flex justify-content-between align-items-center flex-wrap">
                    <span style={{ wordBreak: 'break-all', paddingRight: '10px' }}>
                      {index + 1}) {result.link}
                    </span>
                    
                    {result.status === 'pending' && <i className="fa fa-spinner fa-spin text-muted"></i>}
                    
                    {result.status !== 'pending' && (
                       <span className={`alert ${result.status === 'success' ? 'alert-success' : 'alert-danger'} py-1 px-2 m-0 mt-1 mt-sm-0`} style={{fontSize: '0.8em'}}>
                        {result.message}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}